import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatApiError, formatINR, todayISO } from "@/lib/format";
import { payWithRazorpay } from "@/lib/razorpay";
import { useAuth } from "@/context/AuthContext";
import DropUpload from "@/components/DropUpload";
import RuleBuilder from "@/components/RuleBuilder";
import { toast } from "sonner";
import {
  Megaphone,
  Plus,
  Eye,
  Upload,
  Send,
  Sparkles,
  TrendingUp,
  CheckCircle2,
  Clock,
  XCircle,
  FileImage,
  Target,
  CreditCard,
  BarChart3,
  Share2,
  Copy,
  Check,
} from "lucide-react";

const STATUS_TONE = {
  draft: "bg-white/5 text-zinc-300 border-white/10",
  pending_review: "bg-blue-500/10 text-blue-300 border-blue-500/30",
  approved: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  running: "bg-[#D4B878]/15 text-[#E8D6A3] border-[#D4B878]/30",
  rejected: "bg-red-500/10 text-red-400 border-red-500/30",
  completed: "bg-zinc-500/10 text-zinc-300 border-zinc-500/20",
};

export default function AdsDashboard() {
  const [campaigns, setCampaigns] = useState([]);
  const [hubs, setHubs] = useState([]);
  const [open, setOpen] = useState(false);
  const [rulesFor, setRulesFor] = useState(null);
  const { user } = useAuth();

  const load = async () => {
    const [a, h] = await Promise.all([
      api.get("/ads/campaigns/mine"),
      api.get("/hubs"),
    ]);
    setCampaigns(a.data);
    setHubs(h.data);
  };
  useEffect(() => {
    load();
  }, []);

  const submit = async (id) => {
    try {
      await api.post(`/ads/campaigns/${id}/submit`);
      toast.success("Submitted for review");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const topUp = async (c) => {
    const raw = window.prompt(`Top-up amount (INR) for "${c.name}":`, "1000");
    if (!raw) return;
    const amt = Number(raw);
    if (!amt || amt < 100) {
      toast.error("Minimum top-up is ₹100");
      return;
    }
    await payWithRazorpay({
      purpose: "ad_budget_topup",
      amount_inr: amt,
      campaign_id: c.id,
      user,
      description: `Top-up · ${c.name}`,
      onSuccess: load,
    });
  };

  const totalImpr = campaigns.reduce((a, c) => a + (c.impressions || 0), 0);
  const totalSpent = campaigns.reduce((a, c) => a + (c.spent || 0), 0);
  const running = campaigns.filter((c) => c.status === "running").length;

  return (
    <div className="space-y-8" data-testid="ads-dashboard">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-[#E8D6A3] mb-3">
            <Megaphone size={12} className="inline mr-1" /> DOOH advertising
          </div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter">
            Your campaigns.
          </h1>
          <p className="text-zinc-400 mt-2">
            Book premium screen real-estate across India&apos;s Mirrorloops network.
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <a
            href="/app/campaigns/new"
            data-testid="ad-guided-builder-btn"
            className="inline-flex items-center gap-2 px-4 py-2 border border-[#E8D6A3]/40 text-[#E8D6A3] hover:bg-[#D4B878] hover:text-black transition font-semibold text-sm"
          >
            <Sparkles size={14} /> Guided builder
          </a>
          <NewCampaignDialog open={open} setOpen={setOpen} hubs={hubs} onDone={load} />
        </div>
      </div>

      <div className="grid md:grid-cols-4 gap-4">
        <KPI label="Campaigns" value={campaigns.length} />
        <KPI label="Running now" value={running} tone="gold" />
        <KPI label="Total impressions" value={totalImpr.toLocaleString()} tone="gold" />
        <KPI label="Spent" value={formatINR(totalSpent.toFixed(2))} />
      </div>

      <div className="space-y-3">
        {campaigns.length === 0 && (
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-12 text-center text-zinc-500">
            No campaigns yet. Create your first ad to get started.
          </div>
        )}
        {campaigns.map((c) => (
          <div
            key={c.id}
            data-testid={`ad-row-${c.id}`}
            className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 flex items-center gap-5 flex-wrap"
          >
            <div className="w-28 h-20 rounded-xl overflow-hidden bg-black shrink-0 border border-white/10">
              {c.creative_type === "video" ? (
                <video src={c.creative_url} muted className="w-full h-full object-cover" />
              ) : (
                <img src={c.creative_url} alt={c.name} className="w-full h-full object-cover" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Badge variant="outline" className={`border ${STATUS_TONE[c.status] || ""} capitalize`}>
                  {c.status.replace("_", " ")}
                </Badge>
                <span className="text-xs text-zinc-500">
                  {c.start_date} → {c.end_date}
                </span>
              </div>
              <div className="font-bold text-lg truncate">{c.name}</div>
              <div className="text-xs text-zinc-500 truncate">
                {c.targeted_hub_ids?.length
                  ? `${c.targeted_hub_ids.length} hubs targeted`
                  : "All hubs"}{" "}
                · Budget {formatINR(c.total_budget)} · Spent {formatINR(c.spent?.toFixed(2) || 0)}
              </div>
              {c.review_notes && (
                <div className="text-xs text-red-400 mt-1">
                  HQ notes: {c.review_notes}
                </div>
              )}
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">
                Impressions
              </div>
              <div className="text-2xl font-black text-[#E8D6A3]">
                {c.impressions.toLocaleString()}
              </div>
            </div>
            <div className="flex gap-2">
              <ShareCampaignButton campaign={c} />
              <Button
                onClick={() => setRulesFor(c)}
                variant="outline"
                size="sm"
                data-testid={`ad-rules-btn-${c.id}`}
                className="bg-white/5 border-white/10 rounded-full text-white hover:text-black"
              >
                <Sparkles size={13} className="mr-1.5 text-[#E8D6A3]" />
                Rules
                {c.rules?.length ? (
                  <span className="ml-1.5 px-1.5 py-0.5 bg-[#D4B878] text-black text-[10px] font-bold rounded-full">
                    {c.rules.length}
                  </span>
                ) : null}
              </Button>
              {(c.status === "draft" || c.status === "rejected") && (
                <Button
                  onClick={() => submit(c.id)}
                  data-testid={`ad-submit-${c.id}`}
                  className="bg-[#D4B878] hover:bg-[#E8D6A3] text-black rounded-full"
                >
                  <Send size={14} className="mr-2" /> Submit
                </Button>
              )}
              {(c.status === "running" || c.status === "approved") && (
                <Button
                  onClick={() => topUp(c)}
                  variant="outline"
                  data-testid={`ad-topup-${c.id}`}
                  className="bg-white/5 border-white/10 rounded-full"
                >
                  <CreditCard size={14} className="mr-2" /> Top up
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      <RulesDialog
        campaign={rulesFor}
        onClose={() => setRulesFor(null)}
        onSaved={load}
      />
    </div>
  );
}

function NewCampaignDialog({ open, setOpen, hubs, onDone }) {
  const [form, setForm] = useState({
    name: "",
    creative_url: "",
    creative_type: "image",
    click_url: "",
    advertiser_company: "",
    advertiser_type: "business",
    targeted_hub_ids: [],
    start_date: todayISO(),
    end_date: "2026-12-31",
    total_budget: 5000,
    start_hour: 0,
    end_hour: 24,
    days_of_week: [0, 1, 2, 3, 4, 5, 6],
    rules: [],
  });
  const [pricing, setPricing] = useState(null);
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) =>
    setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const toggleHub = (id) => {
    const s = new Set(form.targeted_hub_ids);
    s.has(id) ? s.delete(id) : s.add(id);
    setForm({ ...form, targeted_hub_ids: Array.from(s) });
  };

  const toggleDow = (d) => {
    const s = new Set(form.days_of_week);
    s.has(d) ? s.delete(d) : s.add(d);
    setForm({ ...form, days_of_week: Array.from(s).sort() });
  };

  useEffect(() => {
    (async () => {
      const ids = form.targeted_hub_ids.join(",");
      const { data } = await api.get(`/ads/pricing?hub_ids=${ids}`);
      setPricing(data);
    })();
  }, [form.targeted_hub_ids]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/ads/campaigns", {
        ...form,
        total_budget: Number(form.total_budget),
        start_hour: Number(form.start_hour),
        end_hour: Number(form.end_hour),
      });
      toast.success("Campaign draft created · submit for review when ready");
      setOpen(false);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const dowLabels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid="new-campaign-btn"
          className="bg-[#D4B878] hover:bg-[#E8D6A3] text-black rounded-full"
        >
          <Plus size={16} className="mr-2" /> New campaign
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#14110E] border-white/10 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create campaign</DialogTitle>
        </DialogHeader>
        <div className="space-y-5">
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2 block">
              I am an…
            </Label>
            <div className="grid grid-cols-3 gap-2" data-testid="ad-advertiser-type">
              {[
                { key: "artist", label: "Artist", desc: "musician · film · brand · creator" },
                { key: "business", label: "Business", desc: "brand · retail · agency" },
                { key: "individual", label: "Person", desc: "cause · community · local" },
              ].map((t) => {
                const on = form.advertiser_type === t.key;
                return (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setForm({ ...form, advertiser_type: t.key })}
                    data-testid={`ad-advertiser-type-${t.key}`}
                    className={`p-3 border text-left transition ${
                      on
                        ? "border-[#E8D6A3] bg-[#D4B878]/10"
                        : "border-white/10 hover:border-white/30"
                    }`}
                  >
                    <div className={`text-sm font-bold ${on ? "text-[#E8D6A3]" : "text-white"}`}>
                      {t.label}
                    </div>
                    <div className="text-[10px] text-zinc-500 mt-1">{t.desc}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Campaign name">
              <Input data-testid="ad-name" value={form.name} onChange={set("name")} className="bg-white/5 border-white/10" />
            </Field>
            <Field label="Advertiser / company">
              <Input data-testid="ad-company" value={form.advertiser_company} onChange={set("advertiser_company")} className="bg-white/5 border-white/10" />
            </Field>
          </div>
          <Field label="Creative (drag-drop image or video)">
            <DropUpload
              kind="ad_creative"
              testid="ad-creative-upload"
              currentUrl={form.creative_url}
              onUploaded={(url, meta) => {
                setForm({
                  ...form,
                  creative_url: url,
                  creative_type: meta?.content_type?.startsWith("video/") ? "video" : "image",
                });
              }}
            />
          </Field>
          <Field label="Click-through URL (optional)">
            <Input data-testid="ad-click" value={form.click_url} onChange={set("click_url")} placeholder="https://" className="bg-white/5 border-white/10" />
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Start date">
              <Input data-testid="ad-start" type="date" value={form.start_date} onChange={set("start_date")} className="bg-white/5 border-white/10" />
            </Field>
            <Field label="End date">
              <Input data-testid="ad-end" type="date" value={form.end_date} onChange={set("end_date")} className="bg-white/5 border-white/10" />
            </Field>
            <Field label="Total budget (INR)">
              <Input data-testid="ad-budget" type="number" min={100} value={form.total_budget} onChange={set("total_budget")} className="bg-white/5 border-white/10" />
            </Field>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-2">
              <Target size={12} /> Time-of-day targeting
            </Label>
            <div className="grid grid-cols-2 gap-3 mt-2">
              <Input type="number" min={0} max={23} value={form.start_hour} onChange={set("start_hour")} data-testid="ad-start-hour" className="bg-white/5 border-white/10" placeholder="Start hour (0-23)" />
              <Input type="number" min={1} max={24} value={form.end_hour} onChange={set("end_hour")} data-testid="ad-end-hour" className="bg-white/5 border-white/10" placeholder="End hour (1-24)" />
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {dowLabels.map((lbl, i) => {
                const on = form.days_of_week.includes(i);
                return (
                  <button
                    type="button"
                    key={i}
                    onClick={() => toggleDow(i)}
                    data-testid={`ad-dow-${i}`}
                    className={`px-3 py-1.5 rounded-full border text-xs font-semibold ${on ? "bg-[#D4B878] text-black border-[#D4B878]" : "bg-white/5 border-white/10 text-zinc-300"}`}
                  >{lbl}</button>
                );
              })}
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Target hubs (empty = all)</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {hubs.map((h) => {
                const on = form.targeted_hub_ids.includes(h.id);
                return (
                  <button
                    key={h.id}
                    type="button"
                    data-testid={`ad-hub-${h.city.toLowerCase()}`}
                    onClick={() => toggleHub(h.id)}
                    className={`px-3 py-1.5 rounded-full border text-sm transition ${on ? "bg-[#D4B878] text-black border-[#D4B878]" : "bg-white/5 border-white/10 text-zinc-300 hover:border-[#D4B878]/50"}`}
                  >
                    {h.city} · {h.format}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="ad-rules-block">
            <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3 block">
              Programmable rules (optional)
            </Label>
            <RuleBuilder
              rules={form.rules}
              onChange={(rules) => setForm({ ...form, rules })}
              simulator
              testidPrefix="ad-rule"
              emptyHint="Attach rules like `WHEN weather=rain SHOW rain creative` — advertising becomes executable logic. Skip if you want a classic campaign."
            />
          </div>

          {pricing && (
            <div className="rounded-2xl border border-[#D4B878]/30 bg-gradient-to-br from-[#1a1508] to-[#0c0c0c] p-5" data-testid="ad-pricing-preview">
              <div className="text-xs uppercase tracking-[0.22em] text-[#E8D6A3] mb-2">
                <BarChart3 size={12} className="inline mr-1" /> Pricing & audience
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <div className="text-2xl font-black">{pricing.total_seats}</div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Seats reached</div>
                </div>
                <div>
                  <div className="text-2xl font-black">{pricing.est_daily_impressions.toLocaleString()}</div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Est. impr/day</div>
                </div>
                <div>
                  <div className="text-2xl font-black">{pricing.est_audience_monthly.toLocaleString()}</div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Monthly audience</div>
                </div>
              </div>
              {pricing.tiers.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {pricing.tiers.map((t) => (
                    <span key={t.hub_id} className="text-[11px] px-2 py-1 rounded-full border border-white/10 bg-black/40">
                      {t.city} · <span className="text-[#E8D6A3] font-bold">₹{t.cpm} CPM</span>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            onClick={submit}
            disabled={busy || !form.name || !form.creative_url}
            data-testid="ad-create-btn"
            className="bg-[#D4B878] hover:bg-[#E8D6A3] text-black rounded-full"
          >
            {busy ? "Creating…" : "Create draft"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function KPI({ label, value, tone }) {
  return (
    <div
      className={`rounded-2xl p-6 border ${
        tone === "gold"
          ? "border-[#D4B878]/30 bg-gradient-to-br from-[#1a1508] to-[#0c0c0c]"
          : "border-white/10 bg-white/[0.02]"
      }`}
    >
      <div className="text-3xl font-black">{value}</div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mt-1">
        {label}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">
        {label}
      </Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}


function RulesDialog({ campaign, onClose, onSaved }) {
  const [rules, setRules] = useState([]);
  const [busy, setBusy] = useState(false);
  const open = Boolean(campaign);

  useEffect(() => {
    if (campaign) setRules(campaign.rules || []);
  }, [campaign]);

  const save = async () => {
    if (!campaign) return;
    setBusy(true);
    try {
      const { data } = await api.patch(
        `/ads/campaigns/${campaign.id}/rules`,
        { rules }
      );
      toast.success(`${data.count} rule${data.count === 1 ? "" : "s"} saved · attached to "${campaign.name}"`);
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="ad-rules-dialog"
        className="bg-[#14110E] border-white/10 text-white max-w-3xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles size={18} className="text-[#E8D6A3]" /> Programmable rules
          </DialogTitle>
          <div className="text-xs text-zinc-500 mt-1">
            Advertising becomes executable logic for{" "}
            <span className="text-white font-semibold">{campaign?.name}</span>.
            Rules apply on top of your existing time-of-day + hub targeting.
          </div>
        </DialogHeader>
        <div className="space-y-5">
          <RuleBuilder
            rules={rules}
            onChange={setRules}
            simulator
            testidPrefix="ad-rules-dialog-rule"
          />
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={onClose}
            data-testid="ad-rules-dialog-cancel"
            className="text-zinc-400"
          >
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={busy}
            data-testid="ad-rules-dialog-save"
            className="bg-[#D4B878] hover:bg-[#E8D6A3] text-black rounded-full"
          >
            {busy ? "Saving…" : `Save ${rules.length} rule${rules.length === 1 ? "" : "s"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ShareCampaignButton({ campaign }) {
  const [open, setOpen] = useState(false);
  const [token, setToken] = useState(campaign.share_token || "");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const shareUrl = token
    ? `${window.location.origin}/preview/campaign/${token}`
    : "";

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/ads/campaigns/${campaign.id}/share`);
      setToken(data.share_token);
      toast.success("Public preview link ready");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    setBusy(true);
    try {
      await api.delete(`/ads/campaigns/${campaign.id}/share`);
      setToken("");
      toast.success("Link revoked");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  const onOpenChange = (v) => {
    setOpen(v);
    if (v && !token) create();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          data-testid={`ad-share-btn-${campaign.id}`}
          className="bg-white/5 border-white/10 rounded-full text-white hover:text-black"
        >
          <Share2 size={13} className="mr-1.5 text-[#E8D6A3]" /> Share
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#14110E] border-white/10 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 size={18} className="text-[#E8D6A3]" /> Share preview link
          </DialogTitle>
          <div className="text-xs text-zinc-500 mt-1">
            A public, read-only preview of{" "}
            <span className="text-white font-semibold">{campaign.name}</span>.
            No login required — safe to send to clients or stakeholders.
          </div>
        </DialogHeader>

        {busy && !token && (
          <div className="text-white/50 text-sm py-4">Generating link…</div>
        )}

        {!busy && !token && (
          <div className="py-4">
            <div className="text-white/50 text-sm mb-4">
              Share link revoked. Generate a fresh one whenever you're ready.
            </div>
            <Button
              onClick={create}
              data-testid={`ad-share-generate-${campaign.id}`}
              className="bg-[#D4B878] hover:bg-[#E8D6A3] text-black rounded-full"
            >
              <Share2 size={14} className="mr-2" /> Generate new link
            </Button>
          </div>
        )}

        {token && (
          <div className="space-y-4">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-2 block">
                Public preview URL
              </Label>
              <div className="flex gap-2">
                <Input
                  data-testid={`ad-share-url-${campaign.id}`}
                  value={shareUrl}
                  readOnly
                  className="bg-white/[0.02] border-white/10 font-mono text-xs"
                />
                <Button
                  onClick={copy}
                  data-testid={`ad-share-copy-${campaign.id}`}
                  variant="outline"
                  className="bg-white/[0.03] border-white/15"
                >
                  {copied ? <Check size={14} /> : <Copy size={14} />}
                </Button>
              </div>
            </div>

            <div className="flex justify-between items-center pt-2">
              <a
                href={shareUrl}
                target="_blank"
                rel="noreferrer noopener"
                data-testid={`ad-share-open-${campaign.id}`}
                className="text-xs text-[#E8D6A3] hover:underline"
              >
                Open preview in new tab →
              </a>
              <Button
                onClick={revoke}
                disabled={busy}
                variant="ghost"
                size="sm"
                data-testid={`ad-share-revoke-${campaign.id}`}
                className="text-red-400 hover:text-red-300"
              >
                Revoke link
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
